/**
 * Search Sheet (Cmd-B modal)
 *
 * - Fetches JSON from /web-api/v1/search-live?format=json
 * - Renders all UI with Tailwind class strings (no CSS layer)
 * - Single-column dense rows, sectioned by type (Brands · Deals · Reviews · Categories)
 * - Filter chips with live counts
 * - Keyboard nav: ↑/↓ to walk rows, ↵ to open, esc to close, ⌘B / Ctrl+B to toggle
 * - Empty state: popular searches as pills (no analytics, server- or fallback-driven)
 *
 * The mobile API endpoint is untouched; the web theme opts into JSON via
 * `format=json` so we can render our own layout in Tailwind.
 */
(function () {
  'use strict';

  /* ─── Tailwind class strings — single source of truth ──────────── */
  const CLS = {
    // section
    sectionWrap: 'bfwt-search-section tw-flex tw-flex-col',
    sectionHead:
      'tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-px-3 tw-pt-4 tw-pb-1.5 first:tw-pt-2',
    sectionTitle:
      'tw-text-[0.6875rem] tw-uppercase tw-tracking-[var(--ls-eyebrow)] tw-font-bold tw-text-tertiary',
    sectionCount: 'tw-text-[0.6875rem] tw-tabular-nums tw-text-tertiary',

    // row
    row: 'bfwt-search-row tw-group tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-card tw-no-underline tw-text-ink tw-transition-colors tw-duration-DEFAULT hover:tw-bg-accent/[0.08]',
    rowActive: 'tw-bg-accent/[0.10]',
    // 4:3 frame for images (matches site image ratio); square for glyph icons.
    rowImgWrap:
      'tw-flex-none tw-w-12 tw-h-9 tw-rounded-card tw-bg-surface tw-overflow-hidden',
    rowGlyphWrap:
      'tw-flex-none tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-card tw-bg-surface tw-text-tertiary',
    rowImg: 'tw-w-full tw-h-full tw-object-cover',
    rowGlyph: 'bf-icon tw-text-[20px]',
    rowBody: 'tw-flex-1 tw-min-w-0',
    rowTitle: 'tw-text-sm tw-font-semibold tw-text-ink tw-leading-snug',
    rowSub:
      'tw-text-xs tw-italic tw-font-light tw-text-tertiary tw-leading-snug tw-mt-0.5',
    rowMeta:
      'tw-hidden md:tw-inline-flex tw-flex-none tw-text-[10.5px] tw-uppercase tw-tracking-wider tw-font-bold tw-text-accent tw-px-2 tw-py-0.5 tw-rounded-pill tw-bg-accent/10 tw-border tw-border-accent/30',
    rowArrow:
      'bf-icon tw-flex-none tw-text-[18px] tw-text-tertiary tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100',
    rowArrowOn: 'tw-opacity-100 tw-text-accent',

    // filter chip
    chipBase:
      'bfwt-search-filter-chip tw-flex-none tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-pill tw-border tw-text-[10.5px] tw-uppercase tw-tracking-wide tw-font-bold tw-cursor-pointer tw-transition-all tw-duration-DEFAULT',
    chipActive:
      'tw-bg-ink tw-text-paper tw-border-ink hover:tw-text-paper',
    chipInactive:
      'tw-bg-transparent tw-text-secondary tw-border-line/40 hover:tw-border-accent/40 hover:tw-text-ink',
    chipCount: 'tw-text-[10.5px] tw-tabular-nums tw-opacity-70',

    // empty / popular
    emptyWrap: 'tw-flex tw-flex-col tw-gap-3 tw-px-3 tw-py-6',
    emptyTitle:
      'tw-text-[0.6875rem] tw-uppercase tw-tracking-[var(--ls-eyebrow)] tw-font-bold tw-text-tertiary tw-px-1',
    pillWrap: 'tw-flex tw-flex-wrap tw-gap-2',
    pill: 'bfwt-search-pill tw-inline-flex tw-items-center tw-px-3.5 tw-py-1.5 tw-rounded-pill tw-bg-surface tw-border tw-border-line/40 tw-text-sm tw-text-ink tw-no-underline tw-cursor-pointer tw-transition-all tw-duration-DEFAULT hover:tw-bg-accent/10 hover:tw-border-accent hover:tw-text-accent hover:tw--translate-y-px',

    // skeleton
    skelWrap: 'tw-flex tw-flex-col tw-gap-1.5 tw-px-1 tw-py-3',
    skelRow: 'tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5',
    skelIcon:
      'tw-flex-none tw-w-9 tw-h-9 tw-rounded-card tw-bg-line/40 tw-animate-pulse',
    skelLine1: 'tw-h-3 tw-rounded tw-bg-line/40 tw-animate-pulse tw-w-2/3',
    skelLine2:
      'tw-h-2.5 tw-rounded tw-bg-line/30 tw-animate-pulse tw-w-1/3 tw-mt-2',

    // no-results
    noResWrap:
      'tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-3 tw-px-6 tw-py-12',
    noResIcon:
      'tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-pill tw-bg-surface tw-border tw-border-line/40 tw-text-tertiary',
    noResTitle: 'tw-text-sm tw-font-medium tw-text-ink',
    noResDesc: 'tw-text-xs tw-text-tertiary tw-max-w-md',

    // see all
    seeAll:
      'tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-mt-3 tw-px-4 tw-py-3 tw-rounded-card tw-no-underline tw-text-[0.8125rem] tw-uppercase tw-tracking-wide tw-font-bold tw-text-secondary tw-bg-ink/[0.04] tw-transition-colors tw-duration-DEFAULT hover:tw-bg-accent/10 hover:tw-text-accent',
  };

  const ICON_BY_TYPE = {
    brand: 'storefront',
    deal: 'local_offer',
    news: 'newspaper',
    post: 'article',
    category: 'category',
    quiz: 'tune',
  };

  const bfIcon = (name, cls = 'tw-text-[18px]') =>
    `<span class="bf-icon ${cls}" aria-hidden="true" style="--bf-icon-url:url('/wp-content/uploads/bf-icons/${escAttr(name)}.svg')"></span>`;

  const LABEL_BY_TYPE = {
    all: 'All',
    brand: 'Brands',
    deal: 'Deals',
    news: 'Pulse News',
    post: 'Guides',
    category: 'Categories',
    quiz: 'Quizzes',
  };



  const escAttr = (s) =>
    String(s === null || s === undefined ? '' : s).replace(
      /[&<>"]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]
    );
  const escHtml = (s) =>
    String(s === null || s === undefined ? '' : s).replace(
      /[&<>]/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]
    );
  const escRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Highlight every occurrence of `term` inside `text` with a <mark>.
  // Operates on plain text — escapes the rest before injecting <mark>
  // so injected HTML is safe.
  const HL_CLS = 'tw-bg-accent/[0.18] tw-text-ink tw-px-0.5 tw-rounded-sm';
  const highlight = (text, term) => {
    const safeText = escHtml(text);
    if (!term || term.length < 2) return safeText;
    try {
      const re = new RegExp('(' + escRegex(term) + ')', 'gi');
      return safeText.replace(re, '<mark class="' + HL_CLS + '">$1</mark>');
    } catch (_) {
      return safeText;
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof bfwtVars === 'undefined' || !bfwtVars.restUrl) return;

    const sheet = document.getElementById('searchSheet');
    if (!sheet) return;
    const input = sheet.querySelector('#bfwt-modal-input');
    const clearBtn = sheet.querySelector('#bfwt-search-clear');
    const filtersBar = sheet.querySelector('#bfwt-search-filters');
    const results = sheet.querySelector('#bfwt-modal-results');
    const aiToggleBtn = sheet.querySelector('#bfwt-search-ai-toggle');
    const directory = sheet.querySelector('#bfwt-search-directory');
    const backBtn = sheet.querySelector('#bfwt-search-back-btn');
    if (!input || !filtersBar || !results) return;

    const apiUrl = bfwtVars.restUrl + 'app-api/v1/search';

    /* ─── State ─────────────────────────────────────────────────── */
    let debounceTimer = 0;
    let currentTerm = '';
    let currentData = null;
    let currentFilter = 'all';
    let highlighted = -1;
    let isAiMode = false;

    const leftIcon = sheet.querySelector('.bfwt-search-icon-el');

    const discoveryStrip = sheet.querySelector('#bfwt-search-discovery-strip');
    const recentQueriesWrap = sheet.querySelector('#bfwt-search-recent-queries');
    const recentQueriesList = sheet.querySelector('#bfwt-search-recent-queries-list');
    const clearRecentQueriesBtn = sheet.querySelector('#bfwt-clear-recent-queries');
    const popularQueriesWrap = sheet.querySelector('#bfwt-search-popular-queries');
    const popularQueriesList = sheet.querySelector('#bfwt-search-popular-queries-list');
    const recentBrandsWrap = sheet.querySelector('#bfwt-search-recent-brands');
    const recentBrandsList = sheet.querySelector('#bfwt-search-recent-brands-list');

    /* ─── GDPR-Friendly Local-Only Recent Searches (Never stored on server/account) ─── */
    const RECENT_SEARCHES_KEY = 'bfwt_recent_searches_v1';
    const MAX_RECENT_SEARCHES = 8;
    let settleCommitTimer = 0;

    const getRecentSearches = () => {
      try {
        const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((s) => String(s || '').trim())
          .filter((s) => s.length >= 2)
          .slice(0, MAX_RECENT_SEARCHES);
      } catch (_) {
        return [];
      }
    };

    const setRecentSearches = (list) => {
      try {
        if (!list || list.length === 0) {
          window.localStorage.removeItem(RECENT_SEARCHES_KEY);
        } else {
          window.localStorage.setItem(
            RECENT_SEARCHES_KEY,
            JSON.stringify(list.slice(0, MAX_RECENT_SEARCHES))
          );
        }
      } catch (_) {}
      renderRecentSearches();
    };

    const saveRecentSearch = (rawTerm, allowPrefixReplace = false) => {
      const clean = String(rawTerm || '').trim();
      if (clean.length < 2) return;
      const lower = clean.toLowerCase();
      let list = getRecentSearches();

      // If user was typing progressively (e.g. "joy" -> "joybuy"), replace the prefix
      if (allowPrefixReplace && list.length > 0) {
        const newestLower = list[0].toLowerCase();
        if (
          lower.startsWith(newestLower) ||
          newestLower.startsWith(lower)
        ) {
          list.shift();
        }
      }

      list = list.filter((item) => item.toLowerCase() !== lower);
      list.unshift(clean);
      setRecentSearches(list.slice(0, MAX_RECENT_SEARCHES));
    };

    const removeRecentSearch = (rawTerm) => {
      const lower = String(rawTerm || '').trim().toLowerCase();
      if (!lower) return;
      const list = getRecentSearches().filter(
        (item) => item.toLowerCase() !== lower
      );
      setRecentSearches(list);
    };

    const clearRecentSearches = () => {
      setRecentSearches([]);
    };

    const renderRecentChipHtml = (term, isConsoleLink = false) => {
      const searchHref = '/?s=' + encodeURIComponent(term);
      const actionEl = isConsoleLink
        ? `<a href="${escAttr(searchHref)}" class="tw-inline-flex tw-items-center tw-gap-1.5 tw-pl-3 tw-pr-1.5 tw-py-1 tw-text-xs tw-font-medium tw-text-ink hover:tw-text-accent tw-no-underline">
            ${bfIcon('history', 'tw-text-[13px] tw-text-tertiary')}
            <span>${escHtml(term)}</span>
          </a>`
        : `<button type="button" data-pill="${escAttr(term)}" class="tw-inline-flex tw-items-center tw-gap-1.5 tw-pl-3 tw-pr-1.5 tw-py-1 tw-bg-transparent tw-border-0 tw-text-xs tw-font-medium tw-text-ink hover:tw-text-accent tw-cursor-pointer">
            ${bfIcon('history', 'tw-text-[13px] tw-text-tertiary')}
            <span>${escHtml(term)}</span>
          </button>`;
      return `<div class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-paper dark:tw-bg-white/[0.04] tw-border tw-border-line/70 tw-text-xs tw-font-medium tw-text-ink tw-transition-all tw-duration-DEFAULT hover:tw-border-accent/50">
        ${actionEl}
        <button type="button" data-remove-recent="${escAttr(term)}" aria-label="Remove ${escAttr(term)}" title="Remove search" class="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-mr-1 tw-rounded-full tw-bg-transparent tw-border-0 tw-text-tertiary hover:tw-text-red-500 hover:tw-bg-ink/[0.06] tw-cursor-pointer tw-transition-colors">
          ${bfIcon('close', 'tw-text-[12px]')}
        </button>
      </div>`;
    };

    const renderRecentSearches = () => {
      const list = getRecentSearches();
      if (recentQueriesWrap && recentQueriesList) {
        if (list.length === 0) {
          recentQueriesWrap.classList.add('tw-hidden');
          recentQueriesList.innerHTML = '';
        } else {
          recentQueriesWrap.classList.remove('tw-hidden');
          recentQueriesList.innerHTML = list
            .map((t) => renderRecentChipHtml(t, false))
            .join('');
        }
      }

      // Also sync standalone /?s= SearchConsoleWidget if present on page
      const consoleWrap = document.getElementById('bfwt-console-recent-searches');
      const consoleList = document.getElementById('bfwt-console-recent-searches-list');
      if (consoleWrap && consoleList) {
        if (list.length === 0) {
          consoleWrap.classList.add('tw-hidden');
          consoleList.innerHTML = '';
        } else {
          consoleWrap.classList.remove('tw-hidden');
          consoleList.innerHTML = list
            .map((t) => renderRecentChipHtml(t, true))
            .join('');
        }
      }

      if (window.bfwtUpdateMobileHeight) {
        window.bfwtUpdateMobileHeight();
      }
    };

    const renderPopularPillsHtml = (terms) =>
      (terms || [])
        .slice(0, 12)
        .map(
          (term) =>
            `<button type="button" data-pill="${escAttr(term)}" class="bfwt-search-pill tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-bg-paper dark:tw-bg-white/[0.04] tw-border tw-border-line/70 tw-text-xs tw-font-medium tw-text-ink tw-cursor-pointer tw-transition-all tw-duration-DEFAULT hover:tw-bg-accent/[0.08] hover:tw-border-accent/50 hover:tw-text-accent">
              ${bfIcon('trending_up', 'tw-text-[13px] tw-text-accent/80')}
              <span>${escHtml(term)}</span>
            </button>`
        )
        .join('');

    const refreshPopularSearches = async () => {
      try {
        const res = await fetch(
          bfwtVars.restUrl + 'app-api/v1/popular-searches',
          { cache: 'no-store' }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data && Array.isArray(data.terms) && data.terms.length > 0) {
          bfwtVars.popularSearches = data.terms;
          if (popularQueriesList) {
            popularQueriesList.innerHTML = renderPopularPillsHtml(data.terms);
          }
          if (popularQueriesWrap) {
            popularQueriesWrap.classList.remove('tw-hidden');
          }
        }
      } catch (_) {}
    };

    const idleBody = sheet.querySelector('#bfwt-search-idle-body');

    const toggleSearchState = (isSearching) => {
      if (isSearching) {
        if (idleBody) idleBody.classList.add('tw-hidden');
        if (directory) directory.classList.add('tw-hidden');
        if (discoveryStrip) discoveryStrip.classList.add('tw-hidden');
        if (recentBrandsWrap) recentBrandsWrap.classList.add('tw-hidden');
        results.classList.remove('tw-hidden');
        if (window.bfwtUpdateMobileHeight) {
          window.bfwtUpdateMobileHeight();
        }
        if (leftIcon) {
          leftIcon.className =
            'bf-icon material-symbols-rounded tw-flex-none tw-text-primary tw-text-[22px] bfwt-search-icon-el';
          leftIcon.setAttribute('data-bf-icon', 'arrow_back');
          leftIcon.textContent = 'arrow_back';
        }
      } else {
        if (idleBody) idleBody.classList.remove('tw-hidden');
        if (directory) directory.classList.remove('tw-hidden');
        if (discoveryStrip) discoveryStrip.classList.remove('tw-hidden');
        renderRecentSearches();
        if (recentBrandsWrap && recentBrandsList && recentBrandsList.children.length > 0) {
          recentBrandsWrap.classList.remove('tw-hidden');
        }
        results.classList.add('tw-hidden');
        results.replaceChildren();
        if (window.bfwtUpdateMobileHeight) {
          window.bfwtUpdateMobileHeight();
        }
        if (leftIcon) {
          if (isAiMode) {
            leftIcon.className =
              'bf-icon material-symbols-rounded tw-flex-none tw-text-accent tw-text-[22px] bfwt-search-icon-el';
            leftIcon.setAttribute('data-bf-icon', 'bf_ai_mode');
            leftIcon.textContent = 'bf_ai_mode';
          } else {
            leftIcon.className =
              'bf-icon material-symbols-rounded tw-flex-none tw-text-tertiary tw-text-[22px] bfwt-search-icon-el';
            leftIcon.setAttribute('data-bf-icon', 'search');
            leftIcon.textContent = 'search';
          }
        }
      }
    };

    /* ─── Renderers ─────────────────────────────────────────────── */
    const buildItemUrl = (it) => {
      if (!it) return '#';
      const rawId = it.wp_id || it.id;
      const numId =
        typeof rawId === 'number'
          ? rawId
          : String(rawId || '').replace(/^[a-z]+_/, '');
      let url = it.url || '';

      if (!url || url === '#') {
        if (it.type === 'brand') {
          url = (it.data && it.data.link) || it.link || '';
        } else if (it.type === 'deal') {
          url =
            (it.data &&
              it.data.parent_brand_info &&
              it.data.parent_brand_info.link) ||
            (it.data && it.data.link) ||
            it.link ||
            '';
        }
      }

      if (it.type === 'deal') {
        const brandLink =
          it.data && it.data.parent_brand_info && it.data.parent_brand_info.link;
        if (brandLink) {
          url = brandLink;
        }
        if (numId && url && !url.includes('#deal-')) {
          try {
            const u = new URL(url, window.location.origin);
            u.hash = 'deal-' + numId;
            url = u.toString();
          } catch (_) {
            url = url.split('#')[0] + '#deal-' + numId;
          }
        }
      }

      if (url && (url.includes('?post_type=') || url.includes('?p='))) {
        return '#';
      }

      return url || '#';
    };

    const renderIcon = (it) => {
      const glyph = ICON_BY_TYPE[it.type] || it.icon || 'tag';
      const userIcon = it.type === 'category' && it.icon ? it.icon : glyph;
      if (it.image_url) {
        const isBrandRelated = ['brand', 'deal'].includes(it.type);
        const imgWrapCls = isBrandRelated
          ? 'tw-flex-none tw-w-10 tw-h-10 tw-rounded-xl tw-bg-white tw-border tw-border-line/60 tw-p-[2px] tw-shadow-sm tw-overflow-hidden tw-flex tw-items-center tw-justify-center'
          : CLS.rowImgWrap;
        const imgCls = isBrandRelated
          ? 'tw-w-full tw-h-full tw-object-cover tw-rounded-[9px]'
          : CLS.rowImg;
        return `<div class="${imgWrapCls}"><img src="${escAttr(it.image_url)}" alt="" loading="lazy" class="${imgCls}" onerror="this.onerror=null;this.parentElement.className='${CLS.rowGlyphWrap}';"></div>`;
      }
      return `<div class="${CLS.rowGlyphWrap}">${bfIcon(userIcon, 'tw-text-[20px]')}</div>`;
    };

    const renderRow = (it) => {
      const cleanTitle = String(it.title || '').replace(/\*\*/g, '');
      const titleHtml = highlight(cleanTitle, currentTerm);
      const subHtml = it.subtitle ? highlight(it.subtitle, currentTerm) : '';
      return `<a href="${escAttr(buildItemUrl(it))}" class="${CLS.row}" data-type="${escAttr(it.type)}" data-id="${escAttr(it.id)}" data-result-row>
                ${renderIcon(it)}
                <div class="${CLS.rowBody}">
                    <div class="${CLS.rowTitle}">${titleHtml}</div>
                    ${subHtml ? `<div class="${CLS.rowSub}">${subHtml}</div>` : ''}
                </div>
                ${it.meta ? `<span class="${CLS.rowMeta}">${escHtml(it.meta)}</span>` : ''}
                ${bfIcon('arrow_forward', CLS.rowArrow)}
            </a>`;
    };

    const renderSection = (type, items, count) => {
      if (!items || !items.length) return '';
      return `<div class="${CLS.sectionWrap}" data-section="${escAttr(type)}">
                <div class="${CLS.sectionHead}">
                    <span class="${CLS.sectionTitle}">${escHtml(LABEL_BY_TYPE[type] || type)}</span>
                    <span class="${CLS.sectionCount}">${count}</span>
                </div>
                ${items.map(renderRow).join('')}
            </div>`;
    };

    /* ─── States ────────────────────────────────────────────────── */
    const showEmpty = () => {
      filtersBar.classList.add('tw-hidden');
      toggleSearchState(false);
      highlighted = -1;
    };

    const showSkeleton = () => {
      toggleSearchState(true);
      if (isAiMode) {
        const uid = 'bfwt-ai-think-' + Math.random().toString(36).slice(2, 7);
        results.innerHTML = `
          <div class="tw-relative tw-overflow-hidden tw-rounded-card tw-border tw-border-line/60 tw-bg-card/85 tw-py-12 tw-px-6 tw-text-center">
            <!-- Signature Northern Lights Edge Light Beam inside Modal -->
            <div class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-[3px]" style="background: linear-gradient(90deg, transparent 0%, #04875D 18%, #34D399 48%, #38BDF8 78%, transparent 100%); background-size: 200% 100%; animation: bfwtEdgeFlow 2.2s linear infinite;"></div>
            <div class="tw-pointer-events-none tw-absolute tw-inset-x-6 tw-top-0 tw-h-14 tw-blur-xl tw-opacity-55" style="background: radial-gradient(ellipse 75% 100% at 50% 0%, rgba(16,185,129,0.32) 0%, rgba(56,189,248,0.24) 55%, transparent 100%);"></div>

            <!-- Elevated Aurora Glass Orb with Self-Drawing >B Monogram + AI Sparkle -->
            <div class="tw-relative tw-mx-auto tw-mb-5 tw-inline-flex tw-items-center tw-justify-center tw-w-[74px] tw-h-[74px] tw-rounded-full" style="background: linear-gradient(135deg, rgba(4,135,93,0.18) 0%, rgba(52,211,153,0.14) 50%, rgba(56,189,248,0.18) 100%); border: 1.15px solid rgba(56,189,248,0.42); box-shadow: 0 0 26px -4px rgba(56,189,248,0.28);">
              <svg class="bfwt-signature-loader__svg" style="width: 44px; height: 21px;" viewBox="0 0 263 125" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="${uid}-grad" x1="0" y1="0" x2="263" y2="125" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#04875D"/>
                    <stop offset="50%" stop-color="#34D399"/>
                    <stop offset="100%" stop-color="#38BDF8"/>
                  </linearGradient>
                </defs>
                <path class="bfwt-sig-track" d="M 11.2 113.5 L 11.2 11.2 L 122 11.2 L 171 62.3 L 122 113.5 Z"/>
                <path class="bfwt-sig-track" d="M 192 114 L 214 11 M 214 11 H 242 C 256 11 261 24 256 38 C 252 50 241 58 225 60 H 203 M 225 60 C 246 61 255 73 250 92 C 245 106 232 114 212 114 H 192"/>
                <path class="bfwt-sig-draw bfwt-sig-draw--tag" stroke="url(#${uid}-grad)" pathLength="100" d="M 11.2 113.5 L 11.2 11.2 L 122 11.2 L 171 62.3 L 122 113.5 Z"/>
                <path class="bfwt-sig-draw bfwt-sig-draw--b" stroke="url(#${uid}-grad)" pathLength="100" d="M 192 114 L 214 11 M 214 11 H 242 C 256 11 261 24 256 38 C 252 50 241 58 225 60 H 203 M 225 60 C 246 61 255 73 250 92 C 245 106 232 114 212 114 H 192"/>
              </svg>
              <svg class="tw-absolute tw-top-3 tw-right-3 tw-w-3 tw-h-3 tw-animate-pulse" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2L14.4 9.6L22 12L14.4 14.4L12 22L9.6 14.4L2 12L9.6 9.6L12 2Z" fill="#34D399"/>
              </svg>
              <span class="tw-absolute tw-bottom-4 tw-right-3 tw-w-1.5 tw-h-1.5 tw-rounded-full tw-animate-ping" style="background-color: #38BDF8; animation-duration: 1.8s;"></span>
            </div>

            <div class="tw-text-lg tw-font-bold tw-tracking-[var(--ls-title-3)] tw-mb-1.5">
              <span class="bfwt-text-aurora">BF Intelligence</span>
            </div>
            <p class="tw-text-sm tw-text-secondary tw-max-w-sm tw-mx-auto tw-leading-relaxed">
              Analysing live deals &amp; guides for the best match…
            </p>
            <p class="tw-mt-3 tw-text-[11px] tw-font-medium tw-text-tertiary/75">
              Powered by Google Gemini · Not affiliated with Google LLC
            </p>
          </div>
        `;
        highlighted = -1;
        if (window.bfwtUpdateMobileHeight) {
          window.bfwtUpdateMobileHeight();
        }
        return;
      }
      const oneRow = `<div class="${CLS.skelRow}">
                <div class="${CLS.skelIcon}"></div>
                <div class="tw-flex-1 tw-min-w-0">
                    <div class="${CLS.skelLine1}"></div>
                    <div class="${CLS.skelLine2}"></div>
                </div>
            </div>`;
      results.innerHTML = `<div class="${CLS.skelWrap}">${oneRow.repeat(5)}</div>`;
      if (window.bfwtUpdateMobileHeight) {
        window.bfwtUpdateMobileHeight();
      }
    };

    const showNoResults = (term) => {
      filtersBar.classList.add('tw-hidden');
      toggleSearchState(true);
      const title = term
        ? `No results for “${escHtml(term)}”`
        : 'Something went wrong';
      const desc = term
        ? 'Try a brand name, category or keyword like “hosting”, “flights”, “VPN”.'
        : 'Please try again in a moment.';
      const popTerms = Array.isArray(bfwtVars.popularSearches)
        ? bfwtVars.popularSearches.slice(0, 8)
        : [];
      const popBlock = popTerms.length
        ? `<div class="tw-mt-4 tw-flex tw-flex-col tw-items-center tw-gap-2 tw-max-w-md">
            <span class="tw-text-[10.5px] tw-font-heading tw-font-bold tw-uppercase tw-tracking-[var(--ls-badge)] tw-text-tertiary">Popular searches</span>
            <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-1.5">${renderPopularPillsHtml(popTerms)}</div>
          </div>`
        : '';
      results.innerHTML = `<div class="${CLS.noResWrap}">
                <div class="${CLS.noResIcon}">${bfIcon('search_off', 'tw-text-[22px]')}</div>
                <div class="${CLS.noResTitle}">${title}</div>
                <div class="${CLS.noResDesc}">${desc}</div>
                ${popBlock}
            </div>`;
      highlighted = -1;
      if (window.bfwtUpdateMobileHeight) {
        window.bfwtUpdateMobileHeight();
      }
    };

    const renderFilters = (totals) => {
      const order = ['all', 'brand', 'deal', 'post', 'category'];
      const counts = {
        all: totals.all || 0,
        brand: totals.brands || 0,
        deal: totals.deals || 0,
        news: totals.news || 0,
        post: totals.posts || 0,
        category: totals.categories || 0,
      };
      const html = order
        .map((k) => {
          if (k !== 'all' && !counts[k]) return '';
          const cls = `${CLS.chipBase} ${currentFilter === k ? CLS.chipActive : CLS.chipInactive}`;
          return `<button type="button" class="${cls}" data-filter="${k}" aria-pressed="${currentFilter === k}">
                    <span>${LABEL_BY_TYPE[k]}</span>
                    <span class="${CLS.chipCount}">${counts[k]}</span>
                </button>`;
        })
        .join('');
      filtersBar.innerHTML = html;
      filtersBar.classList.toggle('tw-hidden', counts.all === 0);
      filtersBar.classList.toggle('tw-flex', counts.all > 0);
    };

    const parseAiReply = (text, results) => {
      if (!text) return '';
      const formatInline = (line) => {
        let s = escHtml(line);
        s = s.replace(/\[(\d+)\s*,\s*(\d+)\]/g, '[$1] [$2]');
        s = s.replace(
          /\*\*([^*]+)\*\*/g,
          '<strong class="tw-font-semibold tw-text-ink">$1</strong>'
        );
        s = s.replace(/\[(\d+)\]/g, (match, numStr) => {
          const num = parseInt(numStr, 10);
          if (num > 0 && results && num <= results.length) {
            const item = results[num - 1];
            if (item) {
              const itemTitle = String(item.title || '').replace(/\*\*/g, '');
              return `<a href="${escAttr(buildItemUrl(item))}" title="${escAttr(itemTitle)}" class="bfwt-liquid-glass-pill tw-inline-flex tw-items-center tw-justify-center tw-w-[18px] tw-h-[18px] tw-mx-0.5 tw-rounded-full tw-bg-ink/[0.08] dark:tw-bg-white/[0.14] tw-border tw-border-ink/15 dark:tw-border-white/25 tw-text-[10px] tw-font-extrabold tw-text-ink tw-no-underline hover:tw-bg-ink/[0.16] dark:hover:tw-bg-white/[0.24] tw-align-middle tw-leading-none tw-transition-colors">${num}</a>`;
            }
          }
          return match;
        });
        return s;
      };

      const lines = String(text).split('\n');
      const blocks = [];
      for (const rawLine of lines) {
        const trimmed = rawLine.trim();
        if (!trimmed) continue;
        if (/^#{2,3}\s+/.test(trimmed)) {
          const hText = trimmed.replace(/^#{2,3}\s+/, '').replace(/\*\*/g, '');
          blocks.push(`<div class="tw-font-heading tw-font-extrabold tw-text-[10.5px] tw-uppercase tw-tracking-[var(--ls-badge)] tw-text-tertiary tw-mt-3.5 tw-mb-1.5">${formatInline(hText)}</div>`);
        } else if (/^[*-]\s+/.test(trimmed)) {
          const bText = trimmed.replace(/^[*-]\s+/, '');
          blocks.push(`<div class="tw-px-3 tw-py-2.5 tw-my-1.5 tw-rounded-xl tw-bg-ink/[0.025] dark:tw-bg-white/[0.04] tw-border tw-border-line/60 tw-text-[13.5px] tw-leading-relaxed">${formatInline(bText)}</div>`);
        } else {
          blocks.push(`<p class="tw-m-0 tw-mb-2.5 last:tw-mb-0 tw-text-[13.5px] tw-leading-relaxed">${formatInline(trimmed)}</p>`);
        }
      }
      return blocks.join('');
    };

    const renderAiSourceRow = (it, index) => {
      const titleClean = String(it.title || '').replace(/\*\*/g, '');
      let imageUrl = it.image_url || null;
      let subtitle = '';
      if (it.type === 'brand') {
        if (!imageUrl && it.data) {
          imageUrl =
            it.data.brand_logo_url ||
            it.data.brand_logo_svg ||
            it.data.logo_url ||
            null;
        }
        subtitle =
          it.data && (it.data.brand_discount || it.data.brand_intro)
            ? it.data.brand_discount || it.data.brand_intro
            : '';
      } else if (it.type === 'deal') {
        if (!imageUrl && it.data && it.data.parent_brand_info) {
          const pb = it.data.parent_brand_info;
          imageUrl =
            pb.brand_logo_url ||
            pb.brand_logo_svg ||
            pb.logo_url ||
            null;
        }
        subtitle =
          it.data &&
          it.data.parent_brand_info &&
          it.data.parent_brand_info.title
            ? it.data.parent_brand_info.title
            : '';
      } else if (it.type === 'news') {
        if (!imageUrl && it.data) {
          imageUrl = it.data.thumbnail_image_url || null;
        }
        subtitle = 'Pulse News Dispatch';
      } else if (it.type === 'post') {
        if (!imageUrl && it.data) {
          imageUrl = it.data.thumbnail_image_url || null;
        }
        subtitle = 'Savings Guide';
      } else if (it.type === 'quiz') {
        subtitle = 'Calculator / Tool';
      }
      const glyph = ICON_BY_TYPE[it.type] || 'tag';
      const isEditorial = ['post', 'news'].includes(it.type);
      let mediaHtml = '';
      if (imageUrl) {
        if (isEditorial) {
          mediaHtml = `<div class="${CLS.rowImgWrap}">
            <img src="${escAttr(imageUrl)}" alt="" loading="lazy" class="${CLS.rowImg}">
          </div>`;
        } else {
          mediaHtml = `<div class="tw-flex-none tw-w-10 tw-h-10 tw-rounded-xl tw-bg-white tw-border tw-border-line/60 tw-p-[2px] tw-shadow-sm tw-overflow-hidden">
            <img src="${escAttr(imageUrl)}" alt="" loading="lazy" class="tw-w-full tw-h-full tw-object-cover tw-rounded-[9px]">
          </div>`;
        }
      } else {
        mediaHtml = `<div class="tw-flex-none tw-flex tw-items-center tw-justify-center ${isEditorial ? 'tw-w-12 tw-h-9' : 'tw-w-10 tw-h-10'} tw-rounded-card tw-bg-surface tw-text-tertiary">
          ${bfIcon(glyph, 'tw-text-[18px]')}
        </div>`;
      }
      return `<a href="${escAttr(buildItemUrl(it))}" class="tw-group tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-rounded-card tw-no-underline tw-text-ink tw-transition-colors tw-duration-DEFAULT hover:tw-bg-ink/[0.04]" data-result-row>
        <div class="tw-flex-none tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-bg-ink/[0.08] tw-text-ink tw-text-[10px] tw-font-bold">
          ${index}
        </div>
        ${mediaHtml}
        <div class="tw-flex-grow tw-min-w-0">
          <div class="tw-text-sm tw-font-semibold tw-text-ink tw-leading-snug">${escHtml(titleClean)}</div>
          ${subtitle ? `<div class="tw-text-xs tw-text-tertiary tw-leading-snug tw-mt-0.5">${escHtml(subtitle)}</div>` : ''}
        </div>
        ${bfIcon('chevron_right', 'tw-flex-none tw-text-[18px] tw-text-tertiary tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100')}
      </a>`;
    };

    const renderAiModeResults = (data) => {
      filtersBar.classList.add('tw-hidden');
      const replyHtml = parseAiReply(data.reply, data.results);
      let sourcesHtml = '';
      if (data.results && data.results.length > 0) {
        sourcesHtml = `<div class="tw-mt-5 tw-flex tw-flex-col tw-gap-2">
          <div class="tw-text-[0.6875rem] tw-uppercase tw-tracking-[var(--ls-eyebrow)] tw-font-bold tw-text-tertiary tw-px-1">Sources & Recommendations</div>
          <div class="tw-flex tw-flex-col tw-gap-1">
            ${data.results.map((item, idx) => renderAiSourceRow(item, idx + 1)).join('')}
          </div>
        </div>`;
      }
      results.innerHTML = `
        <div class="bfwt-ai-reply-bubble bfwt-liquid-glass-bar tw-relative tw-overflow-hidden tw-p-4 sm:tw-p-5 tw-rounded-card tw-border tw-border-line/80 tw-bg-card/95 tw-mb-4" style="background-image: radial-gradient(ellipse 85% 65% at 0% 0%, rgba(4,135,93,0.10) 0%, rgba(14,165,233,0.07) 38%, transparent 68%);">
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2.5 tw-mb-3.5">
            <div class="tw-flex tw-items-center tw-gap-2.5">
              <span class="bfwt-liquid-glass-pill tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-[10px] tw-text-accent tw-border" style="background: linear-gradient(135deg, rgba(4,135,93,0.16), rgba(14,165,233,0.14)); border-color: rgba(56,189,248,0.32);">${bfIcon('bf_ai_mode', 'tw-text-[16px]')}</span>
              <div class="tw-flex tw-flex-col">
                <span class="tw-text-sm tw-font-extrabold tw-tracking-tight tw-text-ink">BF Intelligence</span>
                <span class="tw-text-[11px] tw-text-tertiary">Live UK deal &amp; guide synthesis</span>
              </div>
            </div>
            <span class="tw-text-[9.5px] tw-font-extrabold tw-uppercase tw-tracking-[0.09em] bfwt-text-aurora">AI BRIEF</span>
          </div>
          <div class="tw-text-sm tw-text-ink tw-leading-relaxed">${replyHtml}</div>
          <div class="tw-mt-3.5 tw-pt-2.5 tw-border-t tw-border-line/40 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[10.5px] tw-text-tertiary">
            <span>Powered by Google Gemini · Click any [number] or source card below</span>
          </div>
          <div class="tw-pointer-events-none tw-absolute tw-inset-x-5 tw-bottom-0 tw-h-[1.5px]" style="background: linear-gradient(90deg, transparent 0%, rgba(4,135,93,0.42) 35%, rgba(56,189,248,0.42) 65%, transparent 100%);"></div>
        </div>
        ${sourcesHtml}
      `;
      highlighted = -1;
      if (window.bfwtUpdateMobileHeight) {
        window.bfwtUpdateMobileHeight();
      }
    };

    const showResults = (data) => {
      currentData = data;
      toggleSearchState(true);
      if (isAiMode && data.reply) {
        renderAiModeResults(data);
        return;
      }
      renderFilters(data.totals || {});
      const sections = [];
      const include = (k) => currentFilter === 'all' || currentFilter === k;
      if (include('brand') && data.brands)
        {sections.push(renderSection('brand', data.brands, data.totals.brands));}
      if (include('deal') && data.deals)
        {sections.push(renderSection('deal', data.deals, data.totals.deals));}
      if (include('news') && data.news)
        {sections.push(
          renderSection('news', data.news, data.totals.news)
        );}
      if (include('post') && data.posts)
        {sections.push(renderSection('post', data.posts, data.totals.posts));}
      if (include('category') && data.categories)
        {sections.push(
          renderSection('category', data.categories, data.totals.categories)
        );}
      const filterToPostType = {
        brand: 'brand',
        deal: 'deals',
        news: 'news',
        post: 'post',
      };
      const ptSuffix =
        currentFilter !== 'all' && filterToPostType[currentFilter]
          ? '&post_type=' + filterToPostType[currentFilter]
          : '';
      const seeAll = currentTerm
        ? `<a href="/?s=${encodeURIComponent(currentTerm)}${ptSuffix}" class="${CLS.seeAll}">
                    <span>View all ${data.totals.all || 0} results</span>
                    ${bfIcon('arrow_forward', 'tw-text-[16px]')}
                </a>`
        : '';
      results.innerHTML = sections.join('') + seeAll;
      highlighted = -1;
      if (window.bfwtUpdateMobileHeight) {
        window.bfwtUpdateMobileHeight();
      }
    };

    /* ─── Search ────────────────────────────────────────────────── */
    const performSearch = async (term) => {
      if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.show();
      try {
        let res;
        if (isAiMode) {
          const proxyEndpoint = bfwtVars.restUrl + 'app-api/v1/search-ai';
          res = await fetch(`${proxyEndpoint}?q=${encodeURIComponent(term)}`);
        } else {
          res = await fetch(
            `${apiUrl}?term=${encodeURIComponent(term)}&include_posts=1&format=json`
          );
        }
        if (!res.ok) {
          console.error(
            '[BFWT Search Modal] Fetch response was not OK:',
            res.status,
            res.statusText
          );
          throw new Error('network');
        }
        const data = await res.json();
        if (!isAiMode && (!data || !data.totals || data.totals.all === 0)) {
          showNoResults(term);
          if (typeof window.bfwt_trackEvent === 'function') {
            window.bfwt_trackEvent('search_results', {
              search_term: term,
              results_count: 0,
              event_category: 'search',
            });
          }
          return;
        }
        showResults(data);
        clearTimeout(settleCommitTimer);
        settleCommitTimer = setTimeout(() => {
          if (input && input.value.trim() === term) {
            saveRecentSearch(term, true);
          }
        }, 1200);
        if (typeof window.bfwt_trackEvent === 'function') {
          const count = isAiMode
            ? data.results
              ? data.results.length
              : 0
            : data.totals
              ? data.totals.all
              : 0;
          window.bfwt_trackEvent('search_results', {
            search_term: term,
            results_count: count,
            event_category: 'search',
          });
        }
        if (window.BFWT_Accessibility)
          {window.BFWT_Accessibility.announce('Search results loaded.');}
      } catch (err) {
        console.error('[BFWT Search Modal] performSearch failed:', err);
        showNoResults(null);
      } finally {
        if (window.BFWT_EdgeAura) window.BFWT_EdgeAura.hide();
      }
    };

    /* ─── Keyboard nav ──────────────────────────────────────────── */
    const visibleRows = () =>
      Array.from(results.querySelectorAll('[data-result-row]'));
    const setHighlight = (idx) => {
      const rows = visibleRows();
      if (!rows.length) {
        highlighted = -1;
        return;
      }
      rows.forEach((r) => {
        r.classList.remove(...CLS.rowActive.split(' '));
        const arrow = r.querySelector('.material-symbols-rounded:last-child');
        if (arrow) arrow.classList.remove(...CLS.rowArrowOn.split(' '));
      });
      highlighted = ((idx % rows.length) + rows.length) % rows.length;
      const row = rows[highlighted];
      row.classList.add(...CLS.rowActive.split(' '));
      const arrow = row.querySelector('.material-symbols-rounded:last-child');
      if (arrow) arrow.classList.add(...CLS.rowArrowOn.split(' '));
      row.scrollIntoView({ block: 'nearest' });
    };

    /* ─── Wire events ───────────────────────────────────────────── */
    const syncClearBtn = () => {
      if (!clearBtn) return;
      const isEmpty = input.value.length === 0;
      clearBtn.classList.toggle('tw-hidden', isEmpty);
      input.classList.toggle('tw-pr-14', !isEmpty);
    };

    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        input.value = '';
        currentTerm = '';
        currentData = null;
        clearTimeout(debounceTimer);
        syncClearBtn();
        showEmpty();
        input.focus();
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        if (leftIcon && (leftIcon.getAttribute('data-bf-icon') === 'arrow_back' || leftIcon.textContent === 'arrow_back')) {
          e.preventDefault();
          e.stopPropagation();
          input.value = '';
          currentTerm = '';
          currentData = null;
          clearTimeout(debounceTimer);
          syncClearBtn();
          showEmpty();
          input.focus();
        } else {
          input.focus();
        }
      });
    }

    if (aiToggleBtn) {
      aiToggleBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        isAiMode = !isAiMode;

        const inputWrap = sheet.querySelector('.bfwt-search-input-wrap');
        input.blur();
        const toggleIcon = aiToggleBtn.querySelector('.bfwt-ai-toggle-icon');
        if (isAiMode) {
          aiToggleBtn.className =
            'bfwt-ai-toggle-active tw-flex-none tw-flex tw-items-center tw-justify-center tw-gap-1.5 sm:tw-gap-2 tw-px-3 sm:tw-px-3.5 tw-h-10 sm:tw-h-11 tw-rounded-full tw-border tw-cursor-pointer tw-transition-all tw-duration-DEFAULT tw-text-white hover:tw-opacity-95';
          aiToggleBtn.style.cssText = 'background: linear-gradient(135deg, #04875D 0%, #10B981 46%, #0EA5E9 100%); border-color: transparent; box-shadow: 0 2px 10px rgba(14,165,233,0.22);';
          if (toggleIcon) {
            toggleIcon.classList.remove('tw-text-accent', 'tw-text-indigo-500', 'dark:tw-text-indigo-400');
            toggleIcon.classList.add('tw-text-white');
          }
          const subText = aiToggleBtn.querySelector('.bfwt-ai-toggle-sub');
          if (subText) subText.textContent = 'Using';
          input.placeholder = 'Ask BF Intelligence: tools, flights, deals...';
        } else {
          aiToggleBtn.className =
            'tw-flex-none tw-flex tw-items-center tw-justify-center tw-gap-1.5 sm:tw-gap-2 tw-px-3 sm:tw-px-3.5 tw-h-10 sm:tw-h-11 tw-rounded-full tw-border tw-cursor-pointer tw-transition-all tw-duration-DEFAULT tw-text-ink hover:tw-opacity-95';
          aiToggleBtn.style.cssText = 'background: linear-gradient(135deg, rgba(4,135,93,0.12) 0%, rgba(16,185,129,0.10) 48%, rgba(14,165,233,0.14) 100%); border-color: rgba(14,165,233,0.36);';
          if (toggleIcon) {
            toggleIcon.classList.add('tw-text-accent');
            toggleIcon.classList.remove('tw-text-white');
          }
          const subText = aiToggleBtn.querySelector('.bfwt-ai-toggle-sub');
          if (subText) subText.textContent = 'Switch to';
          input.placeholder = 'Search perks, brands, tools & deals...';
        }

        const term = input.value.trim();
        if (term.length >= 2) {
          showSkeleton();
          performSearch(term);
        } else {
          showEmpty();
        }
      });
    }

    input.addEventListener('input', () => {
      const term = input.value.trim();
      currentTerm = term;
      syncClearBtn();
      clearTimeout(debounceTimer);
      if (term.length < 2) {
        currentData = null;
        showEmpty();
        return;
      }
      const delay = isAiMode ? 600 : 300;
      debounceTimer = setTimeout(() => {
        showSkeleton();
        performSearch(term);
      }, delay);
    });

    input.addEventListener('keydown', (e) => {
      const rows = visibleRows();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (rows.length) setHighlight(highlighted < 0 ? 0 : highlighted + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (rows.length)
          {setHighlight(highlighted < 0 ? rows.length - 1 : highlighted - 1);}
      } else if (e.key === 'Enter') {
        if (highlighted >= 0 && rows[highlighted]) {
          e.preventDefault();
          rows[highlighted].click();
        } else if (currentTerm.length >= 2) {
          e.preventDefault();
          saveRecentSearch(currentTerm, true);
          input.blur();
          if (isAiMode) {
            clearTimeout(debounceTimer);
            showSkeleton();
            performSearch(currentTerm);
          } else {
            window.location.href = '/?s=' + encodeURIComponent(currentTerm);
          }
        }
      }
    });

    // Filter chip click. Stop propagation so the document-level outside-
    // click handler in sheet-manager.js doesn't see this click — the
    // re-render detaches the chip from the DOM, breaking its
    // .closest('.bfwt-floating-sheet') check and closing the sheet.
    filtersBar.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-filter]');
      if (!chip || !currentData) return;
      e.preventDefault();
      e.stopPropagation();
      currentFilter = chip.getAttribute('data-filter');
      showResults(currentData);
    });

    const executePillSearch = (term) => {
      if (!term || term.length < 2) return;
      saveRecentSearch(term, false);
      input.value = term;
      currentTerm = term;
      syncClearBtn();
      showSkeleton();
      performSearch(term);
      if (typeof window.bfwt_trackEvent === 'function') {
        window.bfwt_trackEvent('search_pill_click', {
          search_term: term,
          event_category: 'search',
        });
      }
    };

    if (clearRecentQueriesBtn) {
      clearRecentQueriesBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        clearRecentSearches();
      });
    }

    if (discoveryStrip) {
      discoveryStrip.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-remove-recent]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          removeRecentSearch(removeBtn.getAttribute('data-remove-recent'));
          return;
        }
        const pill = e.target.closest('[data-pill]');
        if (pill) {
          e.preventDefault();
          e.stopPropagation();
          executePillSearch(pill.getAttribute('data-pill'));
        }
      });
    }

    // Standalone /?s= SearchConsoleWidget recent searches interactions
    document.addEventListener('click', (e) => {
      const consoleClear = e.target.closest('[data-clear-recent-searches]');
      if (consoleClear) {
        e.preventDefault();
        clearRecentSearches();
        return;
      }
      const consoleRemove = e.target.closest('#bfwt-console-recent-searches [data-remove-recent]');
      if (consoleRemove) {
        e.preventDefault();
        e.stopPropagation();
        removeRecentSearch(consoleRemove.getAttribute('data-remove-recent'));
      }
    });

    // Pill click in empty / no-results state
    results.addEventListener('click', (e) => {
      const pill = e.target.closest('[data-pill]');
      if (pill) {
        e.preventDefault();
        e.stopPropagation();
        executePillSearch(pill.getAttribute('data-pill'));
        return;
      }
      // Result row click → save recent search & tracking
      const row = e.target.closest('[data-result-row]');
      if (row) {
        if (currentTerm && currentTerm.length >= 2) {
          saveRecentSearch(currentTerm, true);
        }
        if (typeof window.bfwt_trackEvent === 'function') {
          window.bfwt_trackEvent('search_result_click', {
            search_term: currentTerm,
            result_type: row.getAttribute('data-type') || 'link',
            event_category: 'search',
          });
        }
      }
    });

    // Dismiss virtual keyboard on touch / scroll inside results on mobile
    const dismissKeyboard = () => {
      if (document.activeElement === input) {
        input.blur();
      }
    };
    results.addEventListener('touchstart', dismissKeyboard, { passive: true });
    results.addEventListener('touchmove', dismissKeyboard, { passive: true });
    results.addEventListener('scroll', dismissKeyboard, { passive: true });

    // Handle viewport changes (e.g. mobile virtual keyboard show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (
          sheet.classList.contains('is-active') &&
          typeof window.bfwtUpdateMobileHeight === 'function'
        ) {
          window.bfwtUpdateMobileHeight();
        }
      });
    }

    /* ─── Cmd-B / Ctrl-B global shortcut ────────────────────────── */
    document.addEventListener('keydown', (e) => {
      const isB = e.key === 'b' || e.key === 'B';
      if (isB && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        // Don't hijack if user is typing in another input
        const inForm =
          document.activeElement &&
          /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName) &&
          document.activeElement !== input;
        if (inForm) return;
        e.preventDefault();
        if (sheet.classList.contains('is-active')) {
          if (window.sheetManager) window.sheetManager.closeAll();
        } else {
          const trigger = document.querySelector(
            '[data-sheet-target="searchSheet"]'
          );
          if (window.sheetManager) window.sheetManager.open(sheet, trigger);
        }
      }
    });

    /* ─── Initial state ─────────────────────────────────────────── */
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = (urlParams.get('s') || '').trim();
      if (urlQuery.length >= 2) {
        saveRecentSearch(urlQuery, true);
      }
    } catch (_) {}
    syncClearBtn();
    showEmpty();
    refreshPopularSearches();
  });
})();
